import {
  useSendOrdersEmailMutation,
  useSendWebHookMutation,
} from "@features/admin/admin-slice"
import { useSendPushNotificationMutation } from "../native/native-slice"
import {
  formatSendProcessingEmailFn,
  formatSendProcessingNotificationFn,
  formatSendDocUploadedEmailFn,
  formatSendDocUploadedNotificationFn,
} from "@features/process-papers/helper"
import { saltUrl } from "@features/admin/process-orders/helper"

type OrderTypes = {
  email?: string
  title?: string
  label?: string
  deviceid?: string
  tenantid?: string
  id?: string
  tenantEndpoint?: string
  clientTransactionId?: string
  data?: {
    reminders?: {
      email?: boolean
      push?: boolean
      whatsapp?: boolean
    }
  }
  tenantMeta: {
    orders?: boolean
    ordersUploadUrl?: boolean
  }
}

type HandleArgs = {
  order: OrderTypes
  orderDescr?: string
  nextStage: string
  updateUrlText?: string
  docsInfo: any[]
  uploadOnly?: boolean
}

export const useOrdersStatus = () => {
  const [sendOrdersEmail] = useSendOrdersEmailMutation()
  const [sendPushNotification] = useSendPushNotificationMutation({})
  const [sendWebHook] = useSendWebHookMutation()

  const sendOrderStatus = async ({
    order,
    orderDescr,
    nextStage,
    docsInfo,
    updateUrlText,
    uploadOnly,
  }: HandleArgs) => {
    // extract an array of {title, url }from docsInfo where url.length > 0
    const urls = docsInfo
      .filter((doc: any) => doc?.upload?.url?.length > 0)
      .map((doc: any) => ({
        title: doc.title,
        url: saltUrl(doc?.upload?.url, 365 * 24 * 60), // 1 year duration
      }))

    // If tenant is carpaddy or tenant has no webhook url, notify customer directly; else call partner webhook
    if (
      order?.tenantid === process.env.REACT_APP_CP_TENANT_ID ||
      !order?.tenantEndpoint
    ) {
      if (order.email && order?.data?.reminders?.email !== false) {
        let emailBody
        if (uploadOnly) {
          emailBody = formatSendDocUploadedEmailFn({
            to: order.email,
            title: order.title,
            label: order.label,
            stage: "uploadOnly",
            urls,
          })
        } else {
          emailBody = formatSendProcessingEmailFn({
            to: order.email,
            title: order.title,
            label: order.label,
            stage: nextStage,
            urls,
          })
        }
        sendOrdersEmail(emailBody)
      }

      if (order.deviceid && order?.data?.reminders?.push !== false) {
        let notif

        if (uploadOnly) {
          notif = formatSendDocUploadedNotificationFn({
            orderTitle: order.title,
            orderLabel: order.label,
            urls,
          })
        } else {
          notif = formatSendProcessingNotificationFn({
            orderTitle: order.title,
            orderLabel: order.label,
            stage: nextStage,
            urls,
          })
        }
        sendPushNotification({ deviceid: order.deviceid, ...notif })
      }

      if (updateUrlText && order?.data?.reminders?.whatsapp !== false) {
        window.open(updateUrlText, "_blank")
      }
    } else {
      // if a tenant customer and order webhook is true
      if (order?.tenantEndpoint && order?.tenantMeta?.orders !== false) {
        await sendWebHook({
          url: order?.tenantEndpoint,
          status: nextStage,
          orderDescr: orderDescr || "",
          id: order?.id,
          clientTransactionId: order?.clientTransactionId,
          type: "orders",
        })
      }

      // if a tenant customer and file uploaded is true
      if (
        urls.length > 0 &&
        order?.tenantEndpoint &&
        order?.tenantMeta?.ordersUploadUrl !== false
      ) {
        await sendWebHook({
          url: order?.tenantEndpoint,
          id: order?.id,
          clientTransactionId: order?.clientTransactionId,
          type: "upload-url",
          urls,
        })
      }
    }
  }

  return { sendOrderStatus }
}
